import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';

const languages = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'de', name: 'Deutsch' },
  { code: 'zh', name: '中文' },
];

interface LanguageSelectorProps {
  onClose?: () => void;
}

export default function LanguageSelector({ onClose }: LanguageSelectorProps) {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    document.documentElement.lang = languageCode;
    localStorage.setItem('i18nextLng', languageCode);
    setIsOpen(false);
    onClose?.();
  };

  return (
    <div className="relative w-full md:w-auto">
      {/* Desktop Dropdown */}
      <div className="hidden md:block relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg 
            bg-dark-lighter/50 hover:bg-dark-lighter border border-primary/10 
            hover:border-primary/30 transition-all duration-300"
          aria-label="Select Language"
        >
          <Languages className="w-5 h-5 text-primary" />
          <span className="text-white/75">
            {languages.find(lang => lang.code === i18n.language)?.name || 'Language'}
          </span>
        </button>

        <div className={`absolute right-0 mt-2 w-48 py-2 bg-dark-lighter rounded-lg shadow-xl 
          border border-primary/10 transition-all duration-300 z-50
          ${isOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}>
          {languages.map(({ code, name }) => (
            <button
              key={code}
              onClick={() => handleLanguageChange(code)}
              className={`w-full px-4 py-2 text-left hover:bg-primary/10 transition-colors
                ${code === i18n.language ? 'text-primary' : 'text-white/75'}`}
            >
              {name}
            </button>
          ))}
        </div>
      </div>

      {/* Mobile Menu */}
      <div className="md:hidden w-full bg-dark-lighter rounded-lg border border-primary/10">
        {languages.map(({ code, name }) => (
          <button
            key={code}
            onClick={() => handleLanguageChange(code)}
            className={`w-full px-4 py-3 text-left first:rounded-t-lg last:rounded-b-lg
              ${code === i18n.language 
                ? 'bg-primary/10 text-primary' 
                : 'text-white/75 hover:bg-primary/5'} 
              transition-colors`}
          >
            {name}
          </button>
        ))}
      </div>
    </div>
  );
}